import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Outlet, useLocation } from 'react-router-dom'
import EasterEggLoader from '../../components/common/EasterEggLoader'
import ErrorBoundary from '../../components/errors/ErrorBoundary'
import LoadingScreen from '../../components/LoadingScreen'
import SecondaryNav from '../../components/SecondaryNav/SecondaryNav'
import { useScrollRestoration } from '../../hooks'
import { addWindowClass, getScreenSize, removeWindowClass } from '../../util/helpers'
import Footer from './footer/Footer'
import Header from './header/Header'
import MenuSidebar from './menu-sidebar/MenuSidebar'

// Lazy load Easter egg components - only load when needed
const Fireworks = React.lazy(() => import('@fireworks-js/react'))
const Snowfall = React.lazy(() => import('react-snowfall'))

// AdminLTE 4 layout classes on the app root: off-canvas sidebar below the lg breakpoint, icon-only "mini"
// sidebar when collapsed, and the tertiary body background behind the page content. Deliberately not
// `layout-fixed`: that makes .app-main the scroll container, and scroll restoration (useScrollRestoration) and
// the dropdown/select positioning in the app assume the window scrolls, as it did on AdminLTE 3.
const LAYOUT_CLASSES = ['sidebar-expand-lg', 'sidebar-mini', 'bg-body-tertiary']

function Main() {
  const [isAppLoaded] = useState(true)
  const [isMenuSidebarCollapsed, setIsMenuSidebarCollapsed] = useState(false)

  const location = useLocation()
  const [oldLocation, setOldLocation] = useState(location)

  // Restore window scroll on back/forward, scroll to top on new navigations.
  useScrollRestoration()

  const [screenSize, setScreenSize] = useState(getScreenSize())

  useEffect(() => {
    removeWindowClass('register-page')
    removeWindowClass('login-page')
    removeWindowClass('hold-transition')
    removeWindowClass('bg-body-secondary')

    LAYOUT_CLASSES.forEach(addWindowClass)

    return () => {
      LAYOUT_CLASSES.forEach(removeWindowClass)
    }
  }, [])

  // Collapse menu sidebar if location changes

  useEffect(() => {
    if (oldLocation.pathname !== location.pathname) {
      if (screenSize === 'sm' || screenSize === 'xs') {
        setIsMenuSidebarCollapsed(false)
      }
    }
    setOldLocation(location)
  }, [location])

  // From the lg breakpoint up the sidebar is part of the layout and `sidebar-collapse` shrinks it to the
  // icon-only mini sidebar. Below it the sidebar is off-canvas and `sidebar-open` slides it in over the page.
  useEffect(() => {
    removeWindowClass('sidebar-collapse')
    removeWindowClass('sidebar-open')

    if (screenSize === 'lg' || screenSize === 'xl') {
      if (isMenuSidebarCollapsed) {
        addWindowClass('sidebar-collapse')
      }
    }
    else if (isMenuSidebarCollapsed) {
      addWindowClass('sidebar-open')
    }
  }, [screenSize, isMenuSidebarCollapsed])

  window.addEventListener('resize', () => {
    if (screenSize !== getScreenSize()) {
      setScreenSize(getScreenSize())
    }
  })

  // Only enable snow on the 25th or 26th of december
  const snowEnabled = useMemo(() => {
    const today = new Date()
    return (
      today.getMonth() === 11 && (
        today.getDate() === 25
        || today.getDate() === 26
      ))
      || false
  }, [])

  // Only enable fireworks on new years eve
  const fireworksEnabled = useMemo(() => {
    const today = new Date()
    return (
      (today.getMonth() === 11 && today.getDate() === 31)
      || (today.getMonth() === 0 && today.getDate() === 1)
      || false
    )
  }, [])

  const getAppTemplate = useCallback(() => {
    if (!isAppLoaded) {
      return <LoadingScreen loadingText="" />
    }

    return (
      <>
        {snowEnabled && (
          <React.Suspense fallback={<EasterEggLoader />}>
            <Snowfall
              snowflakeCount={250}
              style={{
                zIndex: 9999,
              }}
            />
          </React.Suspense>
        )}

        {fireworksEnabled && (
          <React.Suspense fallback={<EasterEggLoader />}>
            <Fireworks
              options={{
                rocketsPoint: {
                  min: 0,
                  max: 100,
                },
                particles: 200,
                intensity: 10,
              }}
              style={{
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                position: 'fixed',
                pointerEvents: 'none',
                zIndex: 9999,
              }}
            />
          </React.Suspense>
        )}

        <Header sidebar={{
          get: isMenuSidebarCollapsed,
          set: setIsMenuSidebarCollapsed,
        }}
        />

        <MenuSidebar sidebar={{
          get: isMenuSidebarCollapsed,
          set: setIsMenuSidebarCollapsed,
        }}
        />

        <main className="app-main">
          <SecondaryNav />

          <div className="app-content">
            <ErrorBoundary>
              <Outlet />
            </ErrorBoundary>
          </div>
        </main>

        <Footer />
        <div
          className="sidebar-overlay"
          role="presentation"
          onClick={() => setIsMenuSidebarCollapsed(prev => !prev)}
        />
      </>
    )
  }, [isAppLoaded])

  return <div className="app-wrapper">{getAppTemplate()}</div>
}

export default Main
